import React, {useEffect, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import DateTimePicker from "common/src/components/widget/DateTimePicker";
import Select from "common/src/components/widget/Select";
import Input from "common/src/components/widget/Input";
import Button from "common/src/components/widget/Button";
import {
  addDays,
  dateToLocalizedFormat,
  dateToString,
  daysBetweenDates,
  durationInDays, toDuration, trimSeconds
} from "common/src/utils/DateUtils";
import Table from "common/src/components/widget/Table";
import PropTypes from "prop-types"
import LocationSearch from "common/src/components/widget/LocationSearch";
import TimePicker from "common/src/components/widget/TimePicker";
import ApiClient from "common/src/api/ApiClient";
import apiClient from "common/src/api/ApiClient";
import PromiseBuilder from "common/src/utils/PromiseUtils";
import {useNotifications} from "common/src/hooks/notification/notificationHook";
import {useTable} from "common/src/hooks/table/tableHook";
import TablePagination from "common/src/components/widget/TablePagination";
import TableFilter from "common/src/components/widget/TableFilter";
import moment from "moment";
import CalculatedPrice from "common/src/components/widget/CalculatedPrice";
import MultiSelect from "common/src/components/widget/MultiSelect";
import Modal from "common/src/components/widget/Modal";
import {offerReservationStatus} from "../../model/offerReservationStatus";
import {serviceType} from "../../model/serviceType";
import {
  POSITIVE_NUMBER,
  roundToDecimalPlaces
} from "common/src/utils/NumberUtils";
import {activityCategory} from "common/src/model/activityCategory";
import {toEuroCurrency} from "common/src/utils/CurrencyUtils";
import {bikeType} from "common/src/model/bikeType";
import AdditionalStatusInfoButton
  from "common/src/components/widget/AdditionalStatusInfoButton";

function TourPlanCard(props) {
  const {
    values,
    getField,
    setValue,
    setValues,
    setErrors,
  } = props.form;

  const {t} = useTranslation();

  const serviceTypes = useMemo(() => {
    return serviceType.map(function (type) {
      return {
        ...type,
        name: t(type.name).toUpperCase()
      }
    })
  }, [])

  const isOfferDisabled = useMemo(() => {
    if (props.offerDetails == null) {
      return false;
    }
    return props.offerDetails.status === 'BOOKING_PENDING' ||
      props.offerDetails.status === 'BOOKED' ||
      props.offerDetails.status === 'REJECTED'
  }, [props.offerDetails])

  const {
    addErrorNotification,
    addWarningNotification,
  } = useNotifications();

  const [selectingServiceFormField, setSelectingServiceFormField] = useState(null);
  const [dayToDelete, setDayToDelete] = useState({popupShown: false});
  const [serviceToDelete, setServiceToDelete] = useState({popupShown: false});
  const [fetchedStages, setFetchedStages] = useState(null);

  useEffect(() => {
    if (props.offerDetails !== null) {
      fetchOffer();
      return;
    }

    setValues({
      "startDate": dateToString(new Date()),
      "days": [{
        stages: [],
        services: [{}]
      }]
    });
  }, [props.offerDetails?.id])

  useEffect(() => {
    updateDayDates();
  }, [values.days, values.startDate])

  function updateDayDates() {
    if (!values.days || !values.startDate) {
      return;
    }
    values.days.forEach((day, dayIndex) => {
      const newDate = dateToString(addDays(new Date(values.startDate), dayIndex), true);
      if (values.days[dayIndex].date !== newDate) {
        setValue(`days[${dayIndex}.date`, newDate, undefined, null);
      }
    })
  }

  function addService(day) {
    let serviceList = getField(`days[${day}].services`).value;
    serviceList.push({});
    setValue(`days[${day}].services`, serviceList)
    setErrors({});
  }

  function removeService() {
    setServiceToDelete({popupShown: false})
    let serviceList = getField(`days[${serviceToDelete.dayIndex}].services`).value;
    serviceList.splice(serviceToDelete.serviceIndex, 1);
    setValue(`days.${serviceToDelete.dayIndex}.services`, serviceList);
    setErrors({});
  }

  function addDay(dayIndex) {
    let dayList = getField(`days`).value;

    for (let i = dayIndex; i < dayList.length; i++) {
      const day = dayList[i];
      for (let j = 0; j < day.services.length; j++) {
        const service = day.services[j];
        if (isServiceDisabled(service)) {
          return addWarningNotification(t('offer.add_day_warning'), t("warning"))
        }
      }
    }

    dayList.splice(dayIndex, 0, {stage: null, services: [{}]})
    setValue(`days`, dayList);
    setErrors({});
  }

  function removeDay() {
    setDayToDelete({popupShown: false})
    let dayList = getField(`days`).value;

    for (let i = dayToDelete.dayIndex; i < dayList.length; i++) {
      const day = dayList[i];
      for (let j = 0; j < day.services.length; j++) {
        const service = day.services[j];
        if (isServiceDisabled(service)) {
          return addWarningNotification(t('offer.delete_day_warning'), t("warning"))
        }
      }
    }

    if (dayList.length === 1) {
      return;
    }
    dayList.splice(dayToDelete.dayIndex, 1);
    setValue(`days`, dayList);
    setErrors({});
  }


  function fetchOffer() {
    new PromiseBuilder()
      .then(async () => ApiClient.get(`/api/v1/tour/offer/${props.offerDetails.id}`))
      .then(async (res) => {
        fillForm(res.data.payload);
      })
      .catch((res) => {
        return addErrorNotification(t("toast.fetch_failed_code", {code: res.code}), t("toast.error"));
      })
      .run();
  }

  function fillForm(offerServices) {
    const startDate = props.offerDetails.startDate;
    let days = [{
      services: []
    }];

    // Add the required amount of days
    offerServices.accommodations.concat(offerServices.bikeRentals, offerServices.activities, offerServices.transports).map((service) => {
      let serviceEndDate = ('date' in service ? service.date : ('pickUpDate' in service ? service.pickUpDate : service.checkOutDate));
      if ('checkOutDate' in service) {
        serviceEndDate = dateToString(addDays(new Date(serviceEndDate), -1), true)
      }
      const duration = durationInDays(props.offerDetails.startDate, serviceEndDate);
      while (days.length <= duration) {
        days.push({services: [], stages: []});
      }
    })

    offerServices.accommodations.map((accommodationService) => {
      accommodationService.serviceName = accommodationService.service.name;
      const serviceDay = durationInDays(props.offerDetails.startDate, accommodationService.checkInDate);
      const duration = daysBetweenDates(moment(accommodationService.checkOutDate), moment(accommodationService.checkInDate)) - 1;
      accommodationService.type = "accommodation";
      for (let i = 0; i <= duration; i++) {
        days[serviceDay + i].services.push(accommodationService);
      }
    });
    offerServices.activities.map((activityService) => {
      activityService.serviceName = activityService.service.name;
      const serviceDay = durationInDays(props.offerDetails.startDate, activityService.date);
      activityService.numberOfGuests = activityService.numberOfPersons;
      activityService.type = "activity";
      days[serviceDay].services.push(activityService);
    });
    offerServices.bikeRentals.map((bikeRentalService) => {
      bikeRentalService.serviceName = bikeRentalService.service.name;
      const serviceDay = durationInDays(props.offerDetails.startDate, bikeRentalService.pickUpDate);
      bikeRentalService.type = "bike_rental";
      days[serviceDay].services.push(bikeRentalService);
    });
    offerServices.transports.map((transportService) => {
      transportService.serviceName = transportService.service.name;
      const serviceDay = durationInDays(props.offerDetails.startDate, transportService.pickUpDate);
      transportService.type = "transport";
      transportService.numberOfGuests = transportService.numberOfPersons;
      transportService.rejectionReason = transportService.rejectionReason;
      transportService.fromLocation = {
        name: transportService.fromLocation,
        coordinates: transportService.coordinatesFrom
      };
      transportService.toLocation = {
        name: transportService.toLocation,
        coordinates: transportService.coordinatesTo
      };
      days[serviceDay].services.push(transportService);
    });
    let stages = {};
    props.offerDetails.stages.map((stage) => {
      // this while statement is temporary fix for SAOE-337
      // todo: refactor offer details when possible to use more reliable data structure
      while (days.length < stage.day) {
        days.push({
          services: [],
          stages: []
        })
      }
      if (!("stages" in days[stage.day - 1])) {
        days[stage.day - 1].stages = [];
      }
      days[stage.day - 1].stages.push(stage.stage.id);
      if (!(stage.day in stages)) {
        stages[stage.day] = {
          multiselectValues: [],
          multiselectCodes: [],
          multiselectItems: [],
        };
      }
      stages[stage.day].multiselectValues.push(stage.stage.id);
      stages[stage.day].multiselectCodes.push(stage.stage.code);
      stages[stage.day].multiselectItems.push(stage.stage);
    })

    setFetchedStages(stages);
    days.map((day) => {
      if (day.services.length === 0) {
        day.services.push({})
      }
    })

    setValue('startDate', startDate, undefined, false)
    setValue('days', days, undefined, false)

  }

  function isServiceDisabled(service) {
    return (("status" in service) && service.status !== "NOT_BOOKED") || isOfferDisabled;
  }

  return (
    <div className="offer-details">
      <div className="card">
        <header className="card-header">
          <p className="card-header-title">
            <span className="icon fz fz-sign-out-alt medium mr-1"/>
            <span>{t("offer.tour_plan")}</span>
          </p>
        </header>
        <div className="card-content">
          <DateTimePicker
            className="start-date"
            label={t("offer.start_date")}
            formField={getField("startDate")}
            disabled={isOfferDisabled}
          />
          {getField(`days`).value && getField(`days`).value.map((day, dayIndex) =>
            <div key={dayIndex} className="offer-day">
              <div className="columns is-mobile">
                <div className="column">
                  <span className="icon fz fz-book solid large"/>
                  <span
                    className="day-label">{`Day ${dayIndex + 1} (${dateToLocalizedFormat(addDays(new Date(getField("startDate").value), dayIndex))})`}</span>
                </div>
                <div className="column"></div>
                <div className="column is-narrow mb-2">
                  <div className="dropdown is-hoverable is-right">
                    <div className="dropdown-trigger">
                      <button className="button is-primary" type="button" disabled={['BOOKED', 'REJECTED'].includes(props.offerDetails?.status) || values?.days?.length === 1}>
                              <span className="icon is-small">
                                <i className="fz fz-ellipsis"
                                   aria-hidden="true"></i>
                              </span>
                      </button>
                    </div>
                    { (!['BOOKED', 'REJECTED'].includes(props.offerDetails?.status) && values?.days?.length !== 1) &&
                      <div className="dropdown-menu" id="dropdown-menu"
                         role="menu">
                      <div className="dropdown-content">
                        <a className="dropdown-item"
                           onClick={() => setDayToDelete({
                             popupShown: true,
                             dayIndex: dayIndex
                           })}>Delete this day</a>
                      </div>
                    </div>
                    }
                  </div>
                </div>
              </div>
              <div className="columns">
                <div className="column is-narrow offer-line"></div>
                <div className="column">
                  <div className="columns reverse-row-order">
                    <div className="column is-4-widescreen stage-select">
                      <MultiSelect
                        formField={getField(`days.${dayIndex}.stages`)}
                        endpoint={`/api/v1/booking/stage`}
                        label={t('stages')}
                        selectedValues={fetchedStages && fetchedStages[dayIndex + 1]}
                        disabled={isOfferDisabled}
                      />
                    </div>
                    <div className="column"></div>
                  </div>
                  {day.services.map((service, serviceIndex) =>
                    <TourReservation
                      form={props.form}
                      service={service}
                      key={serviceIndex}
                      dayIndex={dayIndex}
                      serviceIndex={serviceIndex}
                      disabled={isServiceDisabled(service)}
                      isRemovable={['BOOKED', 'REJECTED'].includes(props.offerDetails?.status)}
                      serviceTypes={serviceTypes}
                      setSelectingServiceFormField={setSelectingServiceFormField}
                      setServiceToDelete={setServiceToDelete}
                    />
                  )}
                  <Button className="mb-2" label={t("offer.add_service")}
                          color={"primary"}
                          disabled={['BOOKED', 'REJECTED'].includes(props.offerDetails?.status)}
                          onClick={() => addService(dayIndex)}/>
                  <div className={"add-day-button columns is-mobile " +
                    (['BOOKED', 'REJECTED'].includes(props.offerDetails?.status) ? "disabled" : "")}
                       onClick={() => addDay(dayIndex + 1)}>
                    <div className="column line"></div>
                    <div className="column is-narrow plus-container">
                      <div className="plus">
                        <span className="horizontal-line"></span>
                        <span className="vertical-line"></span>
                      </div>
                    </div>
                    <span className="column line"></span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      {selectingServiceFormField !== null &&
        <ServiceModal selectedService={selectingServiceFormField}
                      onClose={() => setSelectingServiceFormField(null)}
                      form={props.form}
        />
      }
      <Modal
        title={t('offer.warning')}
        content={t("offer.delete_day_question")}
        showPopup={dayToDelete.popupShown}
        label={t('offer.delete')}
        onClose={() => setDayToDelete({popupShown: false})}
        onDelete={() => removeDay()}
      />
      <Modal
        title={t('offer.warning')}
        content={t("offer.delete_service_question")}
        showPopup={serviceToDelete.popupShown}
        label={t('offer.delete')}
        onClose={() => setServiceToDelete({popupShown: false})}
        onDelete={() => removeService()}
      />
    </div>
  )
}

const TourReservation = props => {
  const {getField, setValue, values} = props.form;
  const {service, serviceIndex, dayIndex} = props
  const {t} = useTranslation();
  const {
    addErrorNotification,
  } = useNotifications();
  const reservationStatusList = offerReservationStatus();

  function openServiceModal(dayIndex, serviceIndex) {
    if (getField(`days[${dayIndex}].services[${serviceIndex}].type`).value) {
      props.setSelectingServiceFormField({
        serviceType: getField(`days[${dayIndex}].services[${serviceIndex}].type`).value,
        field: `days[${dayIndex}].services[${serviceIndex}]`,
        dayIndex: dayIndex,
        serviceIndex: serviceIndex
      });
    }
  }

  useEffect(() => {
    if (service.type !== 'accommodation' ||
      !service.service ||
      props.disabled ||
      service.id) {
      return;
    }
    const price = service.service.pricingOptions?.find(pricingOption => {
        return pricingOption.quantity === service.numberOfGuests;
    })?.price ?? service.service.price;
    setValue(`days[${dayIndex}].services[${serviceIndex}].price`, price.toFixed(2), undefined, false);
  }, [service.service, service.numberOfGuests])

  useEffect(() => {
    if (service.type !== 'activity' ||
      !service.service ||
      service.numberOfGuests == null ||
      props.disabled ||
      service.id) {
      return;
    }
    const price = service.service.price * service.numberOfGuests;
    setValue(`days[${dayIndex}].services[${serviceIndex}].price`, price, undefined, false);
  }, [service.service, service.numberOfGuests])

  useEffect(() => {
    if (service.type !== 'bike_rental' ||
      !service.service ||
      service.quantity == null ||
      service.returnDate == null ||
      props.disabled ||
      service.id) {
      return;
    }
    const dayDate = values.days[dayIndex].date;
    const numberOfDays = daysBetweenDates(moment(service.returnDate), moment(dayDate));
    const price = service.service.pricePerDay * service.quantity * numberOfDays;
    if (price < 0 || numberOfDays <= 0) {
      return;
    }
    setValue(`days[${dayIndex}].services[${serviceIndex}].price`, price, undefined, false);
  }, [service.service, service.quantity, service.returnDate])

  useEffect(() => {
    if (service.type !== 'transport' ||
      !service.service ||
      service.distance == null ||
      service.numberOfGuests == null ||
      service.numberOfBikes == null ||
      props.disabled ||
      service.id) {
      return;
    }
    const pricePerKM = service.service.pricePerKM;
    let price = (pricePerKM * service.distance);
    const numberOfGuestsAndBikes = (service.numberOfGuests ?? 0) + (service.numberOfBikes ?? 0);
    if (numberOfGuestsAndBikes > 1) {
      price *= numberOfGuestsAndBikes;
    }
    price = roundToDecimalPlaces(price, 2);
    setValue(`days[${dayIndex}].services[${serviceIndex}].price`, price, undefined, false);
  }, [service.service, service.distance, service.numberOfGuests, service.numberOfBikes])

  useEffect(() => {
    if (service.price != null) {
      props.form.triggerValidation(`days[${dayIndex}].services[${serviceIndex}].price`);
    }
  }, [service.price])

  function onServiceTypeChange(dayIndex, serviceIndex) {
    let fieldName = `days[${dayIndex}].services[${serviceIndex}]`
    setValue(fieldName, {});
  }

  function isServiceRemovable(dayIndex) {
    return (["NOT_BOOKED", "NEW", "REJECTED", "CANCELLED", "CONFIRMED"].includes(service.status)
      || props.isRemovable || service.status == null);

  }

  function getDistance(serviceFormField, fromLocation, toLocation) {
    if (!fromLocation || !toLocation) {
      return;
    }
    new PromiseBuilder()
      .then(async () => ApiClient.post(`/api/v1/map/distance`, {
        from: fromLocation,
        to: toLocation
      }))
      .then(async (res) => {
        setValue(`${serviceFormField}.distance`, res.data.payload.distance.toFixed(2), undefined, false)
      })
      .catch(async (res) =>
        addErrorNotification(t("toast.fetch_failed_status", {status: res.response.statusText}), t("toast.error")))
      .run();
  }

  function getServiceIcon(serviceType) {
    let iconName = null;
    switch (serviceType) {
      case "accommodation":
        iconName = "fz-home";
        break;
      case "activity":
        iconName = "fz-calendar-star";
        break;
      case "transport":
        iconName = "fz-van-shuttle";
        break;
      case "bike_rental":
        iconName = "fz-bicycle";
        break;
      default:
        return <span className="service-icon"></span>;
    }
    return <span className={`icon fz large service-icon ${iconName}`}/>
  }

  return (
    <div className={"day-service " + (service.type ? service.type : "")}>
      <div className="columns">
        <div className="column is-narrow mb-2">
          {getServiceIcon(service.type)}
        </div>
        <div className="column">
          <div className="columns is-fullhd">
            <div className="column">
              <div className="columns is-fullhd">
                <div className="column is-3-fullhd">
                  <Select label={t("offer.service_type")}
                          formField={getField(`days[${dayIndex}].services[${serviceIndex}].type`)}
                          onChange={() => onServiceTypeChange(dayIndex, serviceIndex)}
                          disabled={props.disabled}
                          options={props.serviceTypes}/>
                </div>
                <div className="column is-3-fullhd">
                  <Input label={t("offer.select_service")}
                         disabled={(!service.type || props.disabled)}
                         formField={getField(`days[${dayIndex}].services[${serviceIndex}].serviceName`)}
                         onFocus={() => openServiceModal(dayIndex, serviceIndex)}
                  />
                </div>
                <div className="column is-3-fullhd is-marginless">
                  {['accommodation', 'activity'].includes(service.type) &&
                    <div className="columns">
                      <div className="column is-6-fullhd">
                        <Input label={t("offer.guests")}
                               type={"number"}
                               showArrows={true}
                               numberFormat={POSITIVE_NUMBER}
                               formField={getField(`days[${dayIndex}].services[${serviceIndex}].${service.type === 'bike_rental' ? 'quantity' : 'numberOfGuests'}`)}
                               disabled={props.disabled}
                        />
                      </div>
                      <div className="column is-6-fullhd">
                        <CalculatedPrice
                          priceFormula={t(
                            service.type ?
                              (`offer.${service.type}_price_formula`) :
                              ""
                          )}
                          price={getField(`days[${dayIndex}].services[${serviceIndex}].price`)}
                          label={t("offer.price")}
                          disabled={true}
                        />
                      </div>
                    </div>
                  }
                  {service.type === 'bike_rental' &&
                    <DateTimePicker
                      label={t("offer.return_date")}
                      formField={getField(`days[${dayIndex}].services[${serviceIndex}].returnDate`)}
                      disabled={props.disabled}
                    />
                  }
                  {service.type === 'transport' &&
                    <LocationSearch
                      formField={getField(`days[${dayIndex}].services[${serviceIndex}].fromLocation`)}
                      hasCoordinates={true}
                      values={values}
                      label={t("offer.from_location")}
                      onChange={(newValue) => {
                        getDistance(
                          `days[${dayIndex}].services[${serviceIndex}]`,
                          newValue,
                          getField(`days[${dayIndex}].services[${serviceIndex}].toLocation`).value
                        )
                      }}
                      disabled={props.disabled}
                    />
                  }
                </div>
                <div className="column is-3-fullhd">
                  {['bike_rental', 'accommodation', 'activity'].includes(service.type) &&
                    (
                       reservationStatusList.find(c => c.id === service.status)?.id ===  "REJECTED" ?
                          <AdditionalStatusInfoButton label={t("offer.reservation_status")}
                                                      value={service.status == null ? "" : reservationStatusList.find(c => c.id === service.status).name}
                                                      disabled={true}
                                                      icon={"fz-circle-info"}
                                                      rejectionReason={service.rejectionReason}
                                                      className={"additional-status-info-button"}/>
                          :
                          <Input label={t("offer.reservation_status")}
                                 value={service.status == null ? "" : reservationStatusList.find(c => c.id === service.status).name}
                                 disabled={true}/>

                  )
                  }

                  {service.type === 'transport' &&
                    <LocationSearch
                      formField={getField(`days[${dayIndex}].services[${serviceIndex}].toLocation`)}
                      hasCoordinates={true}
                      values={values}
                      label={t("offer.to_location")}
                      onChange={(newValue) => {
                        getDistance(
                          `days[${dayIndex}].services[${serviceIndex}]`,
                          getField(`days[${dayIndex}].services[${serviceIndex}].fromLocation`).value,
                          newValue
                        )
                      }}
                      disabled={props.disabled}
                    />
                  }

                </div>
              </div>
              <div className="columns is-fullhd is-marginless">
                <div className="column is-3-fullhd">
                  {service.type != null &&
                    <Input label={t("offer.reservation_details")}
                           formField={getField(`days[${dayIndex}].services[${serviceIndex}].details`)}
                           disabled={props.disabled}
                    />
                  }

                </div>

                {service.type === 'bike_rental' &&
                  <>
                    <div className="column is-3-fullhd">
                      <div className="columns is-marginless">
                        <div className="column is-6-fullhd">
                          <TimePicker
                            label={t("offer.pickup_time")}
                            formField={getField(`days[${dayIndex}].services[${serviceIndex}].pickUpTime`)}
                            disabled={props.disabled}
                          />
                        </div>
                        <div className="column is-6-fullhd">
                          <TimePicker
                            label={t("offer.return_time")}
                            formField={getField(`days[${dayIndex}].services[${serviceIndex}].returnTime`)}
                            disabled={props.disabled}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="column is-3-fullhd">
                      <div className="columns is-marginless">
                        <div className="column is-6-fullhd">
                          <Input label={t("offer.bikes")}
                                 type={"number"}
                                 showArrows={true}
                                 numberFormat={POSITIVE_NUMBER}
                                 formField={getField(`days[${dayIndex}].services[${serviceIndex}].${service.type === 'bike_rental' ? 'quantity' : 'numberOfGuests'}`)}
                                 disabled={props.disabled}
                          />
                        </div>
                        <div className="column is-6-fullhd">
                          <CalculatedPrice
                            priceFormula={t(
                              service.type ?
                                (`offer.${service.type}_price_formula`) :
                                ""
                            )}
                            price={getField(`days[${dayIndex}].services[${serviceIndex}].price`)}
                            label={t("offer.price")}
                            disabled={true}
                          />
                        </div>
                      </div>
                    </div>
                  </>
                }
                {service.type === 'transport' &&
                  <>
                    <div className="column is-3-fullhd">
                      <div className="columns is-marginless">
                        <div className="column is-6">
                          <TimePicker
                            label={t("offer.pickup_time")}
                            formField={getField(`days[${dayIndex}].services[${serviceIndex}].pickUpTime`)}
                            disabled={props.disabled}
                          />
                        </div>
                        <div className="column is-6">
                          <Input label={t("offer.bikes")}
                                 type={"number"}
                                 showArrows={true}
                                 numberFormat={POSITIVE_NUMBER}
                                 formField={getField(`days[${dayIndex}].services[${serviceIndex}].numberOfBikes`)}
                                 disabled={props.disabled}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="column is-3-fullhd">
                      <div className="columns is-marginless">
                        <div className="column is-6">
                          <Input label={t("offer.guests")}
                                 type={"number"}
                                 numberFormat={POSITIVE_NUMBER}
                                 showArrows={true}
                                 formField={getField(`days[${dayIndex}].services[${serviceIndex}].${service.type === 'bike_rental' ? 'quantity' : 'numberOfGuests'}`)}
                                 disabled={props.disabled}
                          />
                        </div>
                        <div className="column is-6">
                          <CalculatedPrice
                            priceFormula={t(
                              service.type ?
                                (`offer.${service.type}_price_formula`) :
                                ""
                            )}
                            price={getField(`days[${dayIndex}].services[${serviceIndex}].price`)}
                            label={t("offer.price")}
                            disabled={true}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="column is-3-fullhd">
                      {reservationStatusList.find(c => c.id === service.status)?.id ===  "REJECTED" ?
                        <AdditionalStatusInfoButton label={t("offer.reservation_status")}
                                                    value={service.status == null ? "" : reservationStatusList.find(c => c.id === service.status).name}
                                                    disabled={true}
                                                    icon={"fz-circle-info"}
                                                    rejectionReason={service.rejectionReason}
                                                    className={"additional-status-info-button"}/> :
                        <Input label={t("offer.reservation_status")}
                               value={service.status == null ? "" : reservationStatusList.find(c => c.id === service.status).name}
                               disabled={true}
                      />
                      }
                    </div>
                  </>
                }
                {service.type === 'activity' &&
                  <div className="column is-3-fullhd">
                    <div className="columns is-marginless">
                      <div className="column is-6-fullhd">
                        <TimePicker
                          label={t("offer.time")}
                          formField={getField(`days[${dayIndex}].services[${serviceIndex}].time`)}
                          disabled={props.disabled}
                        />
                      </div>
                    </div>
                  </div>
                }
              </div>
            </div>
            <div className="column is-narrow remove-icon has-tooltip" data-tooltip={t('offer.service.remove_service')}>
                          <span className={"icon fz fz-trash medium solid" +
                            (isServiceRemovable(dayIndex) ? "" : " disabled")}
                                onClick={() => {
                                    props.setServiceToDelete({
                                      popupShown: true,
                                      dayIndex: dayIndex,
                                      serviceIndex: serviceIndex
                                    })
                                }}
                          />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

const ServiceModal = props => {
  const {t} = useTranslation();
  const {addErrorNotification} = useNotifications();
  const {
    setValue,
    errors,
    setErrors,
    triggerValidation
  } = props.form
  const [elements, setElements] = useState([]);
  const [totalElements, setTotalElements] = useState(null);
  const [selectedService, setSelectedService] = useState(null);
  const activityCategories = activityCategory();
  const bikeTypes = bikeType();

  let {
    selection,
    onSelection,
    page,
    onPage,
    sort,
    onSort,
    onFilter,
    filter,
  } = useTable({
    page: 0, filter: filter, total: totalElements,
    data: elements, sort: onSort
  }, onRowClick, fetchData);

  function onRowClick(service) {
    setValue(props.selectedService.field + ".serviceName", service.name);
    setValue(props.selectedService.field + ".service", service);
    setSelectedService(service);
    let newErrors = errors;
    if (newErrors?.days?.[props.selectedService.dayIndex]?.services?.[props.selectedService.serviceIndex]?.serviceName) {
      delete newErrors.days[props.selectedService.dayIndex].services[props.selectedService.serviceIndex].serviceName;
      setErrors(newErrors);
    }
  }

  useEffect(() => {
    if (!selectedService) {
      return;
    }
    setSelectedService(null);
    if (props.form.getField(props.selectedService.field + ".numberOfGuests").value != null) {
      triggerValidation(props.selectedService.field + ".numberOfGuests");
    }
    onClose();
  }, [selectedService])

  function onClose() {
    setElements([]);
    props.onClose();
  }

  function fetchData(page, filter, sort) {
    if (!props.selectedService) {
      return;
    }
    new PromiseBuilder()
      .then(() => apiClient.get(`/api/v1/service/${props.selectedService.serviceType.replace("_", "-")}`, null, page, filter, sort))
      .then((res) => {
        const data = res.data.payload;
        const services = data.content;
        setTotalElements(data.totalElements);
        setElements(services);
      })
      .catch((res) => addErrorNotification(t("toast.fetch_failed_code", {code: res.code}), t("toast.error")))
      .run();
  }

  useEffect(() => {
    const close = (e) => {
      if (e.keyCode === 27) {
        onClose();
      }
    }
    window.addEventListener('keydown', close)
    return () => window.removeEventListener('keydown', close)
  }, [])

  const serviceColumns = {
    transport: [
      {name: t("offer.service.name"), id: "name"},
      {name: t("offer.service.service_provider"), id: "companyName", disableSort: true},
      {name: t("offer.service.location"), id: "location"},
      {name: t("offer.service.quantityAvailable"), id: "quantityAvailable"},
      {name: t("offer.service.passengerCapacity"), id: "passengerCapacity"},
      {name: t("offer.service.bikeCapacity"), id: "bikeCapacity"},
      {name: t("offer.service.pricePerKM"), id: "pricePerKM", cellComponent: (service) => <div>{toEuroCurrency(service.pricePerKM)}</div>},
      {
        name: t("offer.service.additionalInformation"),
        id: "additionalInformation"
      },
    ],
    accommodation: [
      {name: t("offer.service.name"), id: "name"},
      {name: t("offer.service.service_provider"), id: "companyName", disableSort: true},
      {name: t("offer.service.location"), id: "location"},
      {name: t("offer.service.quantityAvailable"), id: "quantityAvailable"},
      {name: t("offer.service.numberOfGuests"), id: "numberOfGuests"},
      {name: t("offer.service.numberOfStars"), id: "numberOfStars"},
      {name: t("offer.service.propertyType"), id: "propertyType"},
      {name: t("offer.service.price"), id: "price", cellComponent: (service) => <div>{toEuroCurrency(service.price)}</div>},
      {
        name: t("offer.service.additionalInformation"),
        id: "additionalInformation"
      },
    ],
    bike_rental: [
      {name: t("offer.service.name"), id: "name"},
      {name: t("offer.service.service_provider"), id: "companyName", disableSort: true},
      {name: t("offer.service.location"), id: "location"},
      {name: t("offer.service.quantity_available"), id: "quantityAvailable"},
      {name: t("offer.service.size"), id: "size"},
      {name: t("offer.service.type"), id: "bikeType", cellComponent: (service) => <div>{bikeTypes.find(t => t.id === service.bikeType).name}</div>},
      {name: t("offer.service.pricePerDay"), id: "pricePerDay", cellComponent: (service) => <div>{toEuroCurrency(service.pricePerDay)}</div>},
      {name: t("offer.service.openingTime"), id: "openingTime", cellComponent: (service) => <div>{trimSeconds(service.openingTime)}</div>},
      {name: t("offer.service.closingTime"), id: "closingTime", cellComponent: (service) => <div>{trimSeconds(service.closingTime)}</div>},
      {
        name: t("offer.service.additionalInformation"),
        id: "additionalInformation"
      },
    ],
    activity: [
      {name: t("offer.service.name"), id: "name"},
      {name: t("offer.service.service_provider"), id: "companyName", disableSort: true},
      {name: t("offer.service.location"), id: "location"},
      {name: t("offer.service.quantityAvailable"), id: "quantityAvailable"},
      {name: t("offer.service.category"), id: "category", cellComponent: (service) => <div>{activityCategories.find(c => c.id === service.category).name}</div>},
      {name: t("offer.service.duration"), id: "duration", cellComponent: (service) => <div>{toDuration(service.duration)}</div>},
      {name: t("offer.service.price"), id: "price", cellComponent: (service) => <div>{toEuroCurrency(service.price)}</div>},
      {name: t("offer.service.openingTime"), id: "openingTime", cellComponent: (service) => <div>{trimSeconds(service.openingTime)}</div>},
      {name: t("offer.service.closingTime"), id: "closingTime", cellComponent: (service) => <div>{trimSeconds(service.closingTime)}</div>},
      {
        name: t("offer.service.additionalInformation"),
        id: "additionalInformation"
      },
    ]
  }

  const serviceColumnFilters = {
    transport: [
      {
        id: "passengerCapacity",
        type: "number",
        name: 'offer.service.passengerCapacity'
      },
      {
        id: "bikeCapacity",
        type: "number",
        name: 'offer.service.bikeCapacity'
      },
    ]
    ,
    accommodation: [
      {
        id: "numberOfGuests",
        type: "number",
        name: 'offer.service.numberOfGuests'
      },
      {
        id: "numberOfStars",
        type: "number",
        name: 'offer.service.numberOfStars'
      },
      {
        id: "propertyType",
        type: "string",
        name: 'offer.service.propertyType'
      },

    ],
    bike_rental: [
      {id: "size", type: "string", name: 'offer.service.size'},
      {id: "bikeType", type: "string", name: 'offer.service.bike_type'},

    ],
    activity: [
      {id: "category", type: "string", name: 'offer.service.category'},
    ]
  }

  const currentServiceColumnFilters = serviceColumnFilters[props.selectedService.serviceType];

  if (!props.selectedService) {
    return null;
  }
  return (
    <div className="modal">
      <div className="modal-background not-visible" onClick={onClose}></div>
      <div className="modal-card">
        <header className="modal-card-head">
          <p
            className="modal-card-title">{t(`offer.service.select_${props.selectedService.serviceType}`)}</p>
        </header>
        <section className="modal-card-body">
          <Table columns={serviceColumns[props.selectedService.serviceType]}
                 data={elements}
                 onSelection={onSelection}
                 selection={selection}
                 onSort={onSort}
                 sort={sort}
          />
          <TablePagination
            page={page.page}
            total={page.total}
            pageSize={page.size}
            onPage={onPage}
          />

          <div className={"level-right"}>
            <TableFilter columns={[
              {id: "name", type: "string", name: t('offer.service.name')},
              {
                id: "location",
                type: "string",
                name: 'offer.service.location'
              },
              {
                id: "quantityAvailable",
                type: "number",
                name: 'offer.service.quantityAvailable'
              },
              ...currentServiceColumnFilters
            ]} filter={filter} onFilter={onFilter}/>
          </div>
        </section>
        <footer className="modal-card-foot">
          <Button
            label={t("offer.cancel")}
            color="primary"
            isOutlined={true}
            onClick={() => onClose()}
          />
        </footer>
      </div>
    </div>
  )
}

ServiceModal.propTypes = {
  form: PropTypes.object,
  selectedService: PropTypes.object,
  onClose: PropTypes.func,
}


export default TourPlanCard;
